import { useState, useEffect, useCallback, createContext, useContext } from "react";
import type { SsoStatus, User } from "@/types/api";
import { ApiUnavailableError } from "@/api/client";
import { createInitialAdmin, getHealth, login as apiLogin, logout as apiLogout, getMe } from "@/api/endpoints";
import type { BootstrapAdminRequest, LoginRequest } from "@/types/api";

interface AuthState {
  user: User | null;
  loading: boolean;
  authenticated: boolean;
  apiStartingUp: boolean;
  apiRetryCount: number;
  setupRequired: boolean;
  recoveryAdminConfigured: boolean;
  sso: SsoStatus;
}

interface AuthActions {
  login: (req: LoginRequest) => Promise<void>;
  bootstrapAdmin: (req: BootstrapAdminRequest) => Promise<void>;
  logout: () => Promise<void>;
  retryApiConnection: () => void;
}

type AuthContextValue = AuthState & AuthActions;

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthProvider(): AuthContextValue {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiStartingUp, setApiStartingUp] = useState(false);
  const [apiRetryCount, setApiRetryCount] = useState(0);
  const [setupRequired, setSetupRequired] = useState(false);
  const [recoveryAdminConfigured, setRecoveryAdminConfigured] = useState(false);
  const [sso, setSso] = useState<SsoStatus>({ enabled: false });
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!cancelled) {
        setApiRetryCount((value) => value + 1);
      }
      let apiReachable = false;

      try {
        const health = await getHealth();
        if (!cancelled) {
          apiReachable = true;
          setSetupRequired(Boolean(health.data.attributes.setup_required));
          setRecoveryAdminConfigured(Boolean(health.data.attributes.recovery_admin_configured));
          setSso(health.data.attributes.sso ?? { enabled: false });
        }
      } catch (err) {
        if (!(err instanceof ApiUnavailableError) && !cancelled) {
          setApiStartingUp(false);
        }
      }

      try {
        const me = await getMe();
        if (!cancelled) {
          apiReachable = true;
          setUser(me.data);
          setSetupRequired(false);
        }
      } catch (err) {
        if (err instanceof ApiUnavailableError) {
          if (!cancelled) {
            setApiStartingUp(!apiReachable);
          }
        } else {
          if (!cancelled) {
            setUser(null);
          }
        }
      } finally {
        if (!cancelled) {
          setApiStartingUp(!apiReachable);
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  useEffect(() => {
    if (!apiStartingUp) {
      return;
    }
    const timer = window.setTimeout(() => {
      setLoading(true);
      setReloadNonce((value) => value + 1);
    }, 3000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [apiStartingUp]);

  const login = useCallback(async (req: LoginRequest) => {
    await apiLogin(req);
    const me = await getMe();
    setUser(me.data);
    setApiStartingUp(false);
    setSetupRequired(false);
  }, []);

  const bootstrapAdmin = useCallback(async (req: BootstrapAdminRequest) => {
    await createInitialAdmin(req);
    const me = await getMe();
    setUser(me.data);
    setApiStartingUp(false);
    setSetupRequired(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      try {
        const me = await getMe();
        setUser(me.data);
      } catch {
        setUser(null);
      }
    }
  }, []);

  const retryApiConnection = useCallback(() => {
    setLoading(true);
    setReloadNonce((value) => value + 1);
  }, []);

  return {
    user,
    loading,
    authenticated: user !== null,
    apiStartingUp,
    apiRetryCount,
    setupRequired,
    recoveryAdminConfigured,
    sso,
    login,
    bootstrapAdmin,
    logout,
    retryApiConnection,
  };
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthContext.Provider");
  }
  return ctx;
}
